import { createContext, useContext, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useConfigurationRowIds } from "./configuration-row-ids";

export const ConfigurationValidation = createContext<(id: string, invalid: boolean) => void>(() => {});

export function useDraftError(invalid: boolean, incomplete = false) {
  const localId = useId();
  const id = `${incomplete ? "draft" : "value"}-${localId}`;
  const report = useContext(ConfigurationValidation);
  useEffect(() => { report(id, invalid); return () => report(id, false); }, [id, invalid, report]);
  return id;
}

// Keep incomplete number input local, and block save/mode changes until corrected.
// Never turn an empty number into zero or a non-finite number into JSON null.
export function ConfigurationNumber({ value, onChange, label, integer = false, min, max, disabled = false, id, onInvalidChange }: {
  value: unknown; onChange: (value: number) => void; label: string; integer?: boolean;
  min?: number; max?: number; disabled?: boolean; id?: string;
  onInvalidChange?: (invalid: boolean) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(String(value ?? ""));
  const previous = useRef(value);
  useEffect(() => {
    if (!Object.is(previous.current, value)) setDraft(String(value ?? ""));
    previous.current = value;
  }, [value]);
  const numeric = Number(draft);
  const invalid = !disabled && (draft.trim() === "" || !Number.isFinite(numeric)
    || (integer && !Number.isSafeInteger(numeric)) || (min !== undefined && numeric < min)
    || (max !== undefined && numeric > max));
  const errorId = useDraftError(invalid, true);
  useEffect(() => { onInvalidChange?.(invalid); }, [invalid, onInvalidChange]);
  return <div className="min-w-0 space-y-1">
    <Input id={id} aria-label={label} type="text" inputMode={integer ? "numeric" : "decimal"}
      className="w-full font-mono" value={draft} disabled={disabled}
      aria-invalid={invalid} aria-describedby={invalid ? errorId : undefined}
      onChange={(event) => {
        const next = event.target.value;
        setDraft(next);
        if (next.trim() !== "" && Number.isFinite(Number(next))) onChange(Number(next));
      }} />
    {invalid && <p id={errorId} className="text-xs text-danger">{t("configurationEditor.numberError", "Enter a valid number within the allowed range.")}</p>}
  </div>;
}

export function ConfigurationKey({ value, keys, onChange, label, allowEmpty = false, singleLine = false }: {
  value: string; keys: string[]; onChange: (key: string) => void; label: string; allowEmpty?: boolean; singleLine?: boolean;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  const input = useRef<HTMLTextAreaElement>(null);
  useEffect(() => setDraft(value), [value]);
  // Guard new identifier edits without rewriting imported keys or nested JSON keys.
  const hasControlCharacters = (next: string) => singleLine && next !== value && /[\p{Cc}\u2028\u2029]/u.test(next);
  const invalidCharacters = hasControlCharacters(draft);
  const invalid = invalidCharacters || (!allowEmpty && !draft.trim()) || (draft !== value && keys.includes(draft));
  const pending = draft !== value;
  const errorId = useDraftError(invalid || pending, true);
  return <div className="min-w-0 space-y-1">
    <Textarea ref={input} aria-label={label} value={draft} rows={Math.max(1, draft.split("\n").length)} className="min-h-9 py-1.5 font-mono" aria-invalid={invalid}
      onKeyDown={(event) => { if (singleLine && event.key === "Enter" && !event.nativeEvent.isComposing) event.preventDefault(); }}
      aria-describedby={invalid ? errorId : undefined} onChange={(event) => {
        const next = event.target.value;
        setDraft(next);
        if (!hasControlCharacters(next) && (allowEmpty || next.trim()) && (next === value || !keys.includes(next))) onChange(next);
      }} />
    {invalid && <p id={errorId} className="text-xs text-danger">{invalidCharacters
      ? t("configurationEditor.keyCharactersError", "Entitlement keys cannot contain line breaks or control characters.")
      : t("configurationEditor.keyError", "Use a unique, non-empty key.")}</p>}
    {!invalid && pending && <Button type="button" variant="outline" size="sm" onClick={() => { onChange(draft); input.current?.focus(); }}>{t("configurationEditor.applyKey", "Apply key name")}</Button>}
  </div>;
}

const jsonTypes = ["string", "number", "boolean", "null", "array", "object"] as const;
type JsonType = typeof jsonTypes[number];
export function jsonValueType(value: unknown): JsonType {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  if (typeof value === "object") return "object";
  if (typeof value === "number" || typeof value === "boolean") return typeof value as JsonType;
  return "string";
}
export function emptyJsonValue(type: string): unknown {
  switch (type) {
    case "number": return 0;
    case "boolean": return false;
    case "null": return null;
    case "array": case "list": return [];
    case "object": return {};
    default: return "";
  }
}

/** A recursive composition of existing controls, preserving JSON types and list order. */
export function ConfigurationValue({ value, onChange, label, fixedType }: {
  value: unknown; onChange: (value: unknown) => void; label: string; fixedType?: JsonType;
}) {
  const { t } = useTranslation();
  const type = fixedType ?? jsonValueType(value);
  const rowIds = useConfigurationRowIds();
  const array = type === "array";
  const container = array || type === "object";
  const invalid = fixedType !== undefined && jsonValueType(value) !== fixedType;
  const errorId = useDraftError(invalid);
  const entries = array ? (Array.isArray(value) ? value.map((v, i) => [String(i), v] as const) : [])
    : type === "object" && value && typeof value === "object" ? Object.entries(value) : [];
  const replace = (key: string, next: unknown) => {
    onChange(array ? entries.map(([k, v]) => k === key ? next : v)
      : Object.fromEntries(entries.map(([k, v]) => [k, k === key ? next : v])));
  };
  return <div className="min-w-0 space-y-2">
    {!fixedType && <Select aria-label={`${label} type`} className="w-36" value={type}
      options={jsonTypes.map((v) => ({ value: v, label: v }))} onValueChange={(next) => onChange(emptyJsonValue(next))} />}
    {invalid && <p id={errorId} className="text-xs text-danger">{t("configurationEditor.typeError", "Value does not match its type. Choose a value of the declared type.")}</p>}
    {type === "number" ? <ConfigurationNumber label={label} value={value} onChange={onChange} />
      : type === "boolean" ? <Select aria-label={label} value={String(value)}
        options={[{ value: "true", label: "true" }, { value: "false", label: "false" }]}
        onValueChange={(next) => onChange(next === "true")} />
      : type === "null" ? <span className="font-mono text-xs">null</span>
      : !container ? <Textarea aria-label={label} value={typeof value === "string" ? value : JSON.stringify(value) ?? ""}
        rows={typeof value === "string" ? Math.max(1, value.split("\n").length) : 1}
        className="min-h-9 py-1.5 font-mono" onChange={(event) => onChange(event.target.value)} />
      : <div className="min-w-0 space-y-3 border-l border-line pl-3">
        {entries.map(([key, item], index) => <div key={rowIds.key(key)} className="min-w-0 space-y-2 border-b border-line pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            {array ? <span className="font-mono text-xs">[{key}]</span>
              : <ConfigurationKey value={key} keys={entries.map(([k]) => k)} label={`${label} key ${index + 1}`} allowEmpty
                onChange={(next) => { rowIds.rename(key, next); onChange(Object.fromEntries(entries.map(([k, v]) => [k === key ? next : k, v]))); }} />}
            <Button type="button" size="sm" variant="ghost" aria-label={`${t("configurationEditor.remove", "Remove")} ${label} ${key}`}
              onClick={() => {
                if (array) rowIds.removeIndex(index, entries.length); else rowIds.remove(key);
                onChange(array ? entries.filter(([k]) => k !== key).map(([, v]) => v)
                  : Object.fromEntries(entries.filter(([k]) => k !== key)));
              }}>{t("configurationEditor.remove", "Remove")}</Button>
          </div>
          <ConfigurationValue value={item} label={`${label}[${JSON.stringify(key)}]`} onChange={(next) => replace(key, next)} />
        </div>)}
        <Button type="button" size="sm" variant="outline" onClick={() => {
          if (array) onChange([...entries.map(([, v]) => v), ""]);
          else {
            let key = "key";
            while (entries.some(([k]) => k === key)) key += "_";
            onChange(Object.fromEntries([...entries, [key, ""]]));
          }
        }}>{t("configurationEditor.addItem", "Add item")}</Button>
      </div>}
  </div>;
}
